// ===== WhatsApp-style Complete Chat Interface =====

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Box } from '@mui/material';
import ChatList from './components/ChatList';
import ChatAndCall from './ChatAndCall';
import CallInterface from './components/CallInterface';
import { socket } from '../../Socket';

const ChatInterface = ({ currentUser, onClose, initialReceiver = null, appointmentData = null }) => {
  const [activeChat, setActiveChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [receiver, setReceiver] = useState(initialReceiver);

  // Initialize chat list with appointment-based messaging
  useEffect(() => {
    console.log('🌍 ChatInterface initializing with receiver:', initialReceiver);
    console.log('🌍 Current user:', currentUser);
    console.log('🌍 Appointment data:', appointmentData);
    
    // Start with empty chat list
    let initialChats = [];
    
    // Handle appointment-based messaging
    if (appointmentData && currentUser) {
      const userRole = currentUser.role || currentUser.userType || 'patient';
      let targetUserId, targetUserName, targetUserRole;
      
      if (userRole === 'doctor') {
        // Doctor sending message to patient
        targetUserId = appointmentData.patientId || appointmentData.patient?._id;
        targetUserName = appointmentData.patient?.name || appointmentData.patientName || 'Patient';
        targetUserRole = 'patient';
      } else {
        // Patient sending message to doctor
        targetUserId = appointmentData.doctorId || appointmentData.doctor?._id;
        targetUserName = appointmentData.doctor?.name || appointmentData.doctorName || 'Doctor';
        targetUserRole = 'doctor';
      }
      
      if (targetUserId) {
        const appointmentChat = {
          id: targetUserId,
          name: targetUserName,
          _id: targetUserId,
          socketId: targetUserId,
          lastMessage: { text: 'Start appointment conversation', timestamp: new Date(), type: 'text' },
          lastMessageTime: new Date(),
          unreadCount: 0,
          isOnline: true, // Assume online for appointment chat
          role: targetUserRole,
          appointmentId: appointmentData._id || appointmentData.id
        };
        
        initialChats = [appointmentChat];
        setActiveChat(appointmentChat);
        setReceiver({
          _id: appointmentChat._id,
          socketId: appointmentChat.socketId,
          name: appointmentChat.name,
          role: appointmentChat.role
        });
        
        console.log('🏥 Set appointment-based chat:', appointmentChat);
      }
    } 
    // Handle direct receiver (non-appointment based)
    else if (initialReceiver) {
      const receiverId = initialReceiver._id || initialReceiver.socketId;
      const receiverName = initialReceiver.name || initialReceiver.username || `User ${receiverId.slice(-4)}`;
      
      const newChat = {
        id: receiverId,
        name: receiverName,
        _id: receiverId,
        socketId: initialReceiver.socketId,
        incomingCallData: initialReceiver.incomingCallData || null,
        lastMessage: { text: 'New conversation', timestamp: new Date(), type: 'text' },
        lastMessageTime: new Date(),
        unreadCount: 0,
        isOnline: true,
        role: initialReceiver.role || 'user'
      };
      
      initialChats = [newChat];
      setActiveChat(newChat);
      setReceiver({
        _id: newChat._id,
        socketId: newChat.socketId,
        name: newChat.name,
        role: newChat.role,
        incomingCallData: newChat.incomingCallData || null
      });

      console.log('🌍 Set direct chat to:', newChat);
    }

    setChats(initialChats);
    console.log('🌍 Initialized chat list:', initialChats);

    // Request actual online users from server only if no appointment data
    if (socket && currentUser?.socketId && !appointmentData) {
      console.log('🌍 Requesting online users from server...');
      socket.emit('get-online-users');
    }

  }, [initialReceiver, currentUser, socket, appointmentData]);

  // Listen for real-time chat updates and user status
  useEffect(() => {
    if (!socket || !currentUser) return;

    const handleNewMessage = (messageData) => {
      console.log('📨 New message received:', messageData);
      // Update the chat list with new message
      setChats(prevChats => {
        const updatedChats = [...prevChats];
        const chatIndex = updatedChats.findIndex(chat => 
          chat._id === messageData.senderId || chat.socketId === messageData.senderId
        );

        if (chatIndex >= 0) {
          // Update existing chat
          updatedChats[chatIndex] = {
            ...updatedChats[chatIndex],
            lastMessage: {
              text: messageData.text,
              timestamp: new Date(messageData.timestamp),
              type: messageData.type || 'text'
            },
            lastMessageTime: new Date(messageData.timestamp),
            unreadCount: activeChat?.id === updatedChats[chatIndex].id ? 
              updatedChats[chatIndex].unreadCount : 
              updatedChats[chatIndex].unreadCount + 1
          };

          // Move to top
          const [updatedChat] = updatedChats.splice(chatIndex, 1);
          updatedChats.unshift(updatedChat);
        } else {
          // Create new chat for unknown sender
          const newChat = {
            id: `chat_${messageData.senderId}`,
            name: messageData.senderName || 'Unknown User',
            _id: messageData.senderId,
            socketId: messageData.senderId,
            lastMessage: {
              text: messageData.text,
              timestamp: new Date(messageData.timestamp),
              type: messageData.type || 'text'
            },
            lastMessageTime: new Date(messageData.timestamp),
            unreadCount: 1,
            isOnline: true,
            role: 'user'
          };
          updatedChats.unshift(newChat);
        }

        return updatedChats;
      });
    };

    const handleUserOnline = (userId) => {
      console.log('🟢 User came online:', userId);
      setChats(prevChats => 
        prevChats.map(chat => 
          chat._id === userId
            ? { ...chat, isOnline: true }
            : chat
        )
      );
    };

    const handleUserOffline = (userId) => {
      console.log('🔴 User went offline:', userId);
      setChats(prevChats => 
        prevChats.map(chat => 
          chat._id === userId
            ? { ...chat, isOnline: false }
            : chat
        )
      );
    };

    const handleUserStatusChange = ({ userId, isOnline }) => {
      console.log('📡 User status change:', { userId, isOnline });
      setChats(prevChats => 
        prevChats.map(chat => 
          chat._id === userId || chat.socketId === userId
            ? { ...chat, isOnline }
            : chat
        )
      );
    };

    // Listen for direct messages (real-time)
    const handleDirectMessage = (message) => {
      console.log('📨 Direct message received:', message);
      
      // Add sender to chat list if not already there
      setChats(prevChats => {
        const existingChat = prevChats.find(chat => chat._id === message.sender);
        if (!existingChat) {
          const newChat = {
            id: message.sender,
            name: message.senderName || `User ${message.sender.slice(-4)}`,
            _id: message.sender,
            socketId: message.sender,
            lastMessage: {
              text: message.text,
              timestamp: new Date(message.timestamp),
              type: message.type || 'text'
            },
            lastMessageTime: new Date(message.timestamp),
            unreadCount: activeChat?._id === message.sender ? 0 : 1,
            isOnline: true,
            role: 'user'
          };
          return [newChat, ...prevChats];
        } else {
          // Update existing chat
          return prevChats.map(chat => 
            chat._id === message.sender ? {
              ...chat,
              lastMessage: {
                text: message.text,
                timestamp: new Date(message.timestamp),
                type: message.type || 'text'
              },
              lastMessageTime: new Date(message.timestamp),
              unreadCount: activeChat?._id === message.sender ? chat.unreadCount : chat.unreadCount + 1,
              isOnline: true
            } : chat
          );
        }
      });
      
      handleNewMessage({
        senderId: message.sender,
        senderName: message.senderName || 'Unknown',
        text: message.text,
        timestamp: message.timestamp,
        type: message.type
      });
    };

    // Handle online users list from server and combine with chat history
    const handleOnlineUsersList = (onlineUsers) => {
      console.log('👥 Received online users list from server:', onlineUsers);
      
      // Filter out current user AND completely exclude problematic offline user
      const availableUsers = onlineUsers.filter(user => 
        user.userId !== currentUser?.socketId && 
        user.userId !== currentUser?._id &&
        user.userId !== '687761d331754ace449e03c7' // Block problematic appointment ID
      );
      
      // Convert online users to chat format
      const onlineUserChats = availableUsers.map(user => ({
        id: user.userId,
        name: user.name || `User ${user.userId.slice(-4)}`,
        _id: user.userId,
        socketId: user.userId,
        lastMessage: { 
          text: '🟢 Online - Ready to chat', 
          timestamp: new Date(), 
          type: 'text' 
        },
        lastMessageTime: new Date(),
        unreadCount: 0,
        isOnline: true,
        role: user.role || 'user'
      }));
      
      // Request chat history to get previous chat partners
      if (socket && currentUser?.socketId) {
        console.log('📜 Requesting chat history for user:', currentUser.socketId);
        socket.emit('get-chat-history', { userId: currentUser.socketId });
      }
      
      // Store online users for merging with chat history
      setChats(onlineUserChats);
      
      console.log('👥 Online users loaded:', onlineUserChats.length);
    };
    
    // Handle chat history response (users who have chatted before)
    const handleChatHistoryResponse = (chatHistory) => {
      console.log('📜 Received chat history:', chatHistory);
      
      if (!chatHistory || !Array.isArray(chatHistory)) {
        console.log('📜 No chat history available');
        return;
      }
      
      // Extract unique chat partners from history
      const chatPartners = new Set();
      const chatRoomsData = {};
      
      chatHistory.forEach(chat => {
        if (chat.userId && chat.userId !== currentUser?.socketId && chat.userId !== currentUser?._id) {
          // Skip problematic appointment ID
          if (chat.userId === '687761d331754ace449e03c7') return;
          
          chatPartners.add(chat.userId);
          
          // Store the latest message for this chat partner
          if (!chatRoomsData[chat.userId] || new Date(chat.timestamp) > new Date(chatRoomsData[chat.userId].timestamp)) {
            chatRoomsData[chat.userId] = {
              lastMessage: chat.lastMessage || 'Previous conversation',
              timestamp: chat.timestamp || new Date(),
              name: chat.name || `User ${chat.userId.slice(-4)}`,
              isOnline: false // Will be updated if user is online
            };
          }
        }
      });
      
      // Merge with existing online users
      setChats(prevChats => {
        const onlineUserIds = prevChats.map(chat => chat._id);
        const mergedChats = [...prevChats];
        
        // Add chat history users who are not currently online
        chatPartners.forEach(partnerId => {
          if (!onlineUserIds.includes(partnerId)) {
            const partnerData = chatRoomsData[partnerId];
            mergedChats.push({
              id: partnerId,
              name: partnerData.name,
              _id: partnerId,
              socketId: partnerId,
              lastMessage: { 
                text: partnerData.lastMessage, 
                timestamp: new Date(partnerData.timestamp), 
                type: 'text' 
              },
              lastMessageTime: new Date(partnerData.timestamp),
              unreadCount: 0,
              isOnline: false,
              role: 'user'
            });
          } else {
            // Update online user with actual last message from history
            const chatIndex = mergedChats.findIndex(chat => chat._id === partnerId);
            if (chatIndex >= 0) {
              const partnerData = chatRoomsData[partnerId];
              mergedChats[chatIndex].lastMessage = {
                text: partnerData.lastMessage,
                timestamp: new Date(partnerData.timestamp),
                type: 'text'
              };
              mergedChats[chatIndex].lastMessageTime = new Date(partnerData.timestamp);
            }
          }
        });
        
        // Sort: Online users first, then by last message time
        mergedChats.sort((a, b) => {
          if (a.isOnline && !b.isOnline) return -1;
          if (!a.isOnline && b.isOnline) return 1;
          return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
        });
        
        console.log('👥 Final merged chat list:', mergedChats);
        return mergedChats;
      });
    };

    // Socket event listeners
    socket.on('receive-direct-message', handleDirectMessage);
    socket.on('message', handleNewMessage);
    socket.on('user-online', handleUserOnline);
    socket.on('user-offline', handleUserOffline);
    socket.on('user-status-change', handleUserStatusChange);
    socket.on('online-users-list', handleOnlineUsersList);
    socket.on('chat-history-response', handleChatHistoryResponse);

    console.log('🌍 Socket listeners attached');

    return () => {
      socket.off('receive-direct-message', handleDirectMessage);
      socket.off('message', handleNewMessage);
      socket.off('user-online', handleUserOnline);
      socket.off('user-offline', handleUserOffline);
      socket.off('user-status-change', handleUserStatusChange);
      socket.off('online-users-list', handleOnlineUsersList);
      socket.off('chat-history-response', handleChatHistoryResponse);
      console.log('🌍 Socket listeners removed');
    };
  }, [socket, currentUser, activeChat]);

  const handleChatSelect = (chat) => {
    console.log('🌍 Chat selected:', chat);
    setActiveChat(chat);
    setReceiver({
      _id: chat._id,
      socketId: chat.socketId,
      name: chat.name,
      role: chat.role
    });

    // Mark messages as read
    setChats(prevChats =>
      prevChats.map(c =>
        c.id === chat.id ? { ...c, unreadCount: 0 } : c
      )
    );
  };

  // Handle message sent from ChatAndCall component
  const handleMessageSent = (message) => {
    setChats(prevChats => {
      const updatedChats = [...prevChats];
      const chatIndex = updatedChats.findIndex(chat => 
        chat._id === message.receiver || chat.socketId === message.receiver
      );

      if (chatIndex >= 0) {
        // Update existing chat
        updatedChats[chatIndex] = {
          ...updatedChats[chatIndex],
          lastMessage: {
            text: message.text,
            timestamp: new Date(message.timestamp),
            type: message.type || 'text'
          },
          lastMessageTime: new Date(message.timestamp)
        };

        // Move to top if it's not already the first
        if (chatIndex > 0) {
          const [updatedChat] = updatedChats.splice(chatIndex, 1);
          updatedChats.unshift(updatedChat);
        }
      }

      return updatedChats;
    });
  };

  const currentReceiver = useMemo(() => {
    if (receiver) return receiver;
    if (activeChat) {
      return {
        _id: activeChat._id,
        socketId: activeChat.socketId,
        name: activeChat.name,
        role: activeChat.role
      };
    }
    return null;
  }, [receiver, activeChat]);

  return (
    <>
      <Box
        style={{
          display: 'flex',
          height: '80vh',
          width: '90vw',
          maxWidth: '1200px',
          maxHeight: '800px',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#fff',
          zIndex: 10000,
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          border: '1px solid #e0e0e0'
        }}
      >
      {/* Left Sidebar - Chat List */}
      <ChatList
        chats={chats}
        activeChat={activeChat}
        onChatSelect={handleChatSelect}
        currentUser={currentUser}
      />

      {/* Right Side - Chat Interface */}
      <Box style={{ 
        flex: 1, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0 // Prevent flex item from overflowing
      }}>
        {activeChat && currentReceiver ? (
          <ChatAndCall
            currentUser={currentUser}
            receiver={currentReceiver}
            socket={socket}
            onClose={onClose}
            onMessageSent={handleMessageSent}
            incomingCallData={currentReceiver?.incomingCallData || null}
            key={activeChat.id} // Force re-render when chat changes
          />
        ) : (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              backgroundColor: '#f8f9fa',
              color: '#666',
              padding: '20px'
            }}
          >
            <Box
              style={{
                fontSize: '72px',
                marginBottom: '24px',
                opacity: 0.3
              }}
            >
              💬
            </Box>
            <h2 style={{ marginBottom: '12px', fontWeight: '300' }}>
              Welcome to Emed4u Chat
            </h2>
            <p style={{ fontSize: '16px', textAlign: 'center', maxWidth: '400px' }}>
              Select a conversation from the left to start messaging with your healthcare providers.
            </p>
            {/* Debug info */}
            <div style={{ marginTop: '20px', fontSize: '12px', opacity: 0.5 }}>
              Active Chat: {activeChat ? 'Yes' : 'No'} | 
              Current Receiver: {currentReceiver ? 'Yes' : 'No'} | 
              Chat Count: {chats.length}
            </div>
          </Box>
        )}
      </Box>
      </Box>
    </>
  );
};

export default ChatInterface;
